import React from 'react';

import type { ITextProps } from './Text.types';

// Forwards its ref so callers can measure the rendered element — e.g.
// `useIsTruncated` deciding whether clipped text needs a tooltip.
export const Text = React.forwardRef<HTMLElement, ITextProps>(({
  children,
  variant = 'text-md',
  weight = 'regular',
  color = 'primary',
  align = 'left',
  as: Component = 'p',
  className = '',
  title,
  onMouseEnter,
  onMouseLeave,
}, ref) => {

  // Font size and line height mapping
  const variantMapper = {
    'display-2xl': 'text-bg-v2-display-2xl leading-bg-v2-display-2xl',
    'display-xl': 'text-bg-v2-display-xl leading-bg-v2-display-xl',
    'display-lg': 'text-bg-v2-display-lg leading-bg-v2-display-lg',
    'display-md': 'text-bg-v2-display-md leading-bg-v2-display-md',
    'display-sm': 'text-bg-v2-display-sm leading-bg-v2-display-sm',
    'display-xs': 'text-bg-v2-display-xs leading-bg-v2-display-xs',
    'text-xl': 'text-bg-v2-xl leading-bg-v2-text-xl',
    'text-lg': 'text-bg-v2-lg leading-bg-v2-text-lg',
    'text-md': 'text-bg-v2-md leading-bg-v2-text-md',
    'text-sm': 'text-bg-v2-sm leading-bg-v2-text-sm',
    'text-xs': 'text-bg-v2-xs leading-bg-v2-text-xs',
  };

  // Font weight mapping
  const weightMapper = {
    'regular': 'font-bg-v2-regular',
    'medium': 'font-bg-v2-medium',
    'semibold': 'font-bg-v2-semibold',
    'bold': 'font-bg-v2-bold',
  };

  // Text color mapping
  const colorMapper = {
    'primary': 'text-text-primary',
    'secondary': 'text-text-secondary',
    'tertiary': 'text-text-tertiary',
    'quaternary': 'text-text-quaternary',
    'brand-primary': 'text-text-brand-primary',
    'brand-secondary': 'text-text-brand-secondary',
    'brand-tertiary': 'text-text-brand-tertiary',
    'white': 'text-text-white',
    'disabled': 'text-text-disabled',
    'error': 'text-text-error-primary',
    'success': 'text-text-success-primary',
    'warning': 'text-text-warning-primary',
    'placeholder': 'text-text-placeholder',
    'avatar-neutral': 'text-avatar-styles-fg-neutral',
    'on-brand': 'text-text-primary-on-brand',
    // Flips with the theme — pairs with a `fg-primary` fill so the chip reads
    // solid-dark on light and solid-light on dark.
    'inverted': 'text-bg-primary',
  };

  // Text alignment mapping
  const alignMapper = {
    'left': 'text-left',
    'center': 'text-center',
    'right': 'text-right',
    'justify': 'text-justify',
  };

  return (
    <Component
      ref={ref as React.Ref<any>}
      className={`font-bg-v2-sans ${variantMapper[variant]} ${weightMapper[weight]} ${colorMapper[color]} ${alignMapper[align]} ${className}`}
      title={title}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </Component>
  );
});

Text.displayName = 'Text';
